import React, { useMemo } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import SelectInput from 'components/common/SelectInput'
import FormInput from 'components/common/FormInput'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import CategorySubtitle from './CategorySubtitle'

const useStyles = makeStyles({
  label: {
    minWidth: 120
  },
  transparentSelect: {
    width: 'auto !important',
    '& .MuiInputBase-root': {
      background: 'transparent !important',
      border: 'none !important',
      height: 'auto !important',
      marginTop: 0,
      width: 'auto !important'
    },
    '& .MuiSelect-select': {
      paddingRight: '24px !important',
      minWidth: '0 !important'
    },
    '& .MuiSelect-icon': {
      color: 'var(--text-color-47)'
    },
    '& .MuiFormControl-root': {
      width: 'auto !important'
    }
  },
  boldValue: {
    color: 'var(--text-color-6) !important',
    fontWeight: 'bold !important'
  },
  placeholderValue: {
    color: 'var(--text-color-11) !important'
  }
})

const RetentionTrackingCategory = ({ categoryValues = {}, onChange, viewMode }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const retainerTypeOptions = useMemo(
    () => [
      { value: 'clear', label: t('dialogs.patientBrief.categories.retentionTracking.retainerTypeOptions.clear') },
      { value: 'hawley', label: t('dialogs.patientBrief.categories.retentionTracking.retainerTypeOptions.hawley') },
      {
        value: 'permanent',
        label: t('dialogs.patientBrief.categories.retentionTracking.retainerTypeOptions.permanent')
      },
      { value: 'other', label: t('dialogs.patientBrief.categories.retentionTracking.retainerTypeOptions.other') }
    ],
    [t]
  )

  const wearTimeOptions = useMemo(
    () => [
      { value: 'fullTime', label: t('dialogs.patientBrief.categories.retentionTracking.wearTimeOptions.fullTime') },
      { value: 'nightTime', label: t('dialogs.patientBrief.categories.retentionTracking.wearTimeOptions.nightTime') },
      { value: 'other', label: t('dialogs.patientBrief.categories.retentionTracking.wearTimeOptions.other') }
    ],
    [t]
  )

  const retainerType = categoryValues.retainerType
  const wearTime = categoryValues.wearTime
  const otherRetainerType = categoryValues.otherRetainerType
  const otherWearTime = categoryValues.otherWearTime

  if (viewMode) {
    return (
      <Grid container direction="column" spacing={1}>
        {retainerType && (
          <Grid item>
            <CategorySubtitle viewMode>
              {t('dialogs.patientBrief.categories.retentionTracking.retainerType')}
            </CategorySubtitle>
            <DazzedParagraph14 strong>
              {retainerType === 'other' && otherRetainerType
                ? otherRetainerType
                : t(`dialogs.patientBrief.categories.retentionTracking.retainerTypeOptions.${retainerType}`)}
            </DazzedParagraph14>
          </Grid>
        )}
        {wearTime && (
          <Grid item>
            <CategorySubtitle viewMode>
              {t('dialogs.patientBrief.categories.retentionTracking.wearTime')}
            </CategorySubtitle>
            <DazzedParagraph14 strong>
              {wearTime === 'other' && otherWearTime
                ? otherWearTime
                : t(`dialogs.patientBrief.categories.retentionTracking.wearTimeOptions.${wearTime}`)}
            </DazzedParagraph14>
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Grid container alignItems="center" wrap="nowrap" spacing={2}>
          <Grid item className={classes.label}>
            <DazzedParagraph14 strong>
              {t('dialogs.patientBrief.categories.retentionTracking.retainerType')}
            </DazzedParagraph14>
          </Grid>
          <Grid item>
            <SelectInput
              value={retainerType}
              options={retainerTypeOptions}
              placeholder={t('general.select')}
              onChange={({ value }) =>
                onChange({
                  retainerType: value,
                  ...(value === 'other' ? {} : { otherRetainerType: '' })
                })
              }
              keepErrorContainerWhenInactive={false}
              style={{}}
              containerClassName={classes.transparentSelect}
              valueClassName={retainerType ? classes.boldValue : classes.placeholderValue}
            />
          </Grid>
        </Grid>
      </Grid>
      {retainerType === 'other' && (
        <Grid item>
          <FormInput
            title={t('dialogs.patientBrief.categories.retentionTracking.otherRetainerType')}
            value={otherRetainerType}
            setValue={value => onChange({ otherRetainerType: value })}
            style={{ bright: true, borderBottom: true }}
            keepErrorContainerWhenInactive={false}
          />
        </Grid>
      )}
      <Grid item>
        <Grid container alignItems="center" wrap="nowrap" spacing={2}>
          <Grid item className={classes.label}>
            <DazzedParagraph14 strong>
              {t('dialogs.patientBrief.categories.retentionTracking.wearTime')}
            </DazzedParagraph14>
          </Grid>
          <Grid item>
            <SelectInput
              value={wearTime}
              options={wearTimeOptions}
              placeholder={t('general.select')}
              onChange={({ value }) =>
                onChange({
                  wearTime: value,
                  ...(value === 'other' ? {} : { otherWearTime: '' })
                })
              }
              keepErrorContainerWhenInactive={false}
              style={{}}
              containerClassName={classes.transparentSelect}
              valueClassName={wearTime ? classes.boldValue : classes.placeholderValue}
            />
          </Grid>
        </Grid>
      </Grid>
      {wearTime === 'other' && (
        <Grid item>
          <FormInput
            title={t('dialogs.patientBrief.categories.retentionTracking.otherWearTime')}
            value={otherWearTime}
            setValue={value => onChange({ otherWearTime: value })}
            style={{ bright: true, borderBottom: true }}
            keepErrorContainerWhenInactive={false}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default RetentionTrackingCategory
